// D-D.6 — phase picker for the time-entry surfaces.
//
// Renders NOTHING unless the selected matter actually has a usable phase, so
// firms that never adopted phases see no new field on any timer form.
//
// Selecting a matter is what loads the list, and switching matters CLEARS a
// stale selection: a phase id from matter A is rejected by the server when the
// entry is saved against matter B (that same-matter check is a security gate,
// not tidiness -- otherwise A's hours roll into B's burn aggregation), so
// leaving it set would hand the user an unexplained 400 at save time.
//
// 'billed' phases are excluded: that phase is already closed out to an invoice,
// so new time should not be attached to it. 'completed' stays selectable
// because a late entry against finished work is normal.

import { useEffect, useState } from 'react'
import api from '../services/api'
import useFieldIds from '../hooks/useFieldIds'

export default function PhasePicker ({ matterId, value, onChange, label = 'Phase', className = '' }) {
  const [phases, setPhases] = useState([])
  // Scoped id: Layout's timer popup and the TimeEntries form can both be open,
  // and a duplicate DOM id sends the label's focus to the wrong control.
  const fid = useFieldIds()

  useEffect(() => {
    let cancelled = false
    if (!matterId) { setPhases([]); return }
    ;(async () => {
      try {
        const r = await api.get(`/api/matter-phases/matter/${matterId}`)
        if (cancelled) return
        const usable = (r.data || []).filter(p => p.status !== 'billed')
        setPhases(usable)
        // Drop a selection that does not belong to the newly-chosen matter.
        if (value && !usable.some(p => String(p.id) === String(value))) onChange(null)
      } catch {
        // Graceful degrade — a phases fetch failure must never block logging time.
        if (!cancelled) setPhases([])
      }
    })()
    return () => { cancelled = true }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [matterId])

  if (!matterId || phases.length === 0) return null

  return (
    <div className={className}>
      <label htmlFor={fid('phase')} className="block text-sm font-medium text-gray-700 mb-1">
        {label} <span className="text-xs font-normal text-gray-400">(optional)</span>
      </label>
      <select
        id={fid('phase')}
        value={value == null ? '' : String(value)}
        onChange={e => onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
      >
        <option value="">— No phase —</option>
        {phases.map(p => (
          <option key={p.id} value={p.id}>
            {p.phase_name}{p.status === 'completed' ? ' (completed)' : ''}
          </option>
        ))}
      </select>
    </div>
  )
}
